import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SIZE_MAP = {
  xl: "w-44 h-44 md:w-56 md:h-56 text-3xl md:text-4xl",
  lg: "w-32 h-32 md:w-40 md:h-40 text-2xl",
  md: "w-24 h-24 md:w-28 md:h-28 text-xl",
  sm: "w-20 h-20 md:w-24 md:h-24 text-base",
};

const ICON_SIZE = {
  xl: "w-20 h-20 md:w-24 md:h-24",
  lg: "w-14 h-14 md:w-16 md:h-16",
  md: "w-10 h-10 md:w-12 md:h-12",
  sm: "w-8 h-8 md:w-10 md:h-10",
};

function resolveIcon(item) {
  if (item.icon_url) {
    return item.icon_url.startsWith("/") ? `${BACKEND}${item.icon_url}` : item.icon_url;
  }
  if (item.slug) return `https://cdn.simpleicons.org/${item.slug}/${item.color || "000"}`;
  return null;
}

function Bubble({ item, index }) {
  const src = resolveIcon(item);
  const size = SIZE_MAP[item.size] || SIZE_MAP.md;
  const iconSize = ICON_SIZE[item.size] || ICON_SIZE.md;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ scale: 1.08, y: -6 }}
      className={`${size} rounded-full bg-white border border-neutral-200 shadow-sm flex flex-col items-center justify-center gap-2 p-3 text-center group cursor-default`}
      data-testid={`tool-bubble-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
      title={item.name}
    >
      {src ? (
        <img src={src} alt={item.name} className={`${iconSize} object-contain`} loading="lazy" />
      ) : (
        <div
          className={`${iconSize} rounded-full flex items-center justify-center font-display font-black tracking-tight`}
          style={{ background: `#${item.color || "0A0A0A"}`, color: "#FFFFFF" }}
        >
          {item.initials || item.name.slice(0, 2)}
        </div>
      )}
      <span className="font-body text-[10px] md:text-[11px] uppercase tracking-[0.15em] text-neutral-600 leading-tight">
        {item.name}
      </span>
    </motion.div>
  );
}

function CodeChip({ item, index }) {
  const src = resolveIcon(item);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="flex flex-col items-center gap-3 group"
      data-testid={`code-chip-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white border border-neutral-200 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-1">
        {src ? (
          <img src={src} alt={item.name} className="w-9 h-9 md:w-11 md:h-11 object-contain" loading="lazy" />
        ) : (
          <span className="font-display font-black text-xl text-neutral-800">{item.initials || item.name.slice(0, 2)}</span>
        )}
      </div>
      <span className="font-body text-xs uppercase tracking-[0.2em] text-neutral-700">{item.name}</span>
    </motion.div>
  );
}

export default function Experience() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/experience").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const tools = items.filter((i) => i.category === "tool").sort((a, b) => a.order - b.order);
  const code = items.filter((i) => i.category === "code").sort((a, b) => a.order - b.order);

  return (
    <section id="experience" className="px-6 md:px-12 lg:px-24 py-32 md:py-48 relative" data-testid="experience-section">
      <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-12">(06) — Experience</p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
        <div className="md:col-span-7">
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="experience-headline">
            Tools I&apos;ve <span className="font-serif-italic font-normal">lived</span> inside —
            and the <span className="font-serif-italic font-normal">languages</span> I speak.
          </h2>
        </div>
        <div className="md:col-span-4 md:col-start-9 flex items-end">
          <p className="font-body text-base text-neutral-700">
            A decade of building, automating and administrating across the Microsoft
            stack, web platforms and design systems. The bubble&apos;s size hints at how
            deep my mileage runs.
          </p>
        </div>
      </div>

      {tools.length > 0 && (
        <div className="mb-24 md:mb-32">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-10">/ Tools &amp; Platforms</p>
          <div className="flex flex-wrap items-center justify-center gap-5 md:gap-7 max-w-5xl mx-auto" data-testid="tools-cloud">
            {tools.map((t, i) => <Bubble key={t.id} item={t} index={i} />)}
          </div>
        </div>
      )}

      {code.length > 0 && (
        <div className="border-t border-neutral-300 pt-16 md:pt-20">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-10">/ Code</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6 md:gap-8" data-testid="code-list">
            {code.map((c, i) => <CodeChip key={c.id} item={c} index={i} />)}
          </div>
        </div>
      )}
    </section>
  );
}
